<template>
  <div
    v-if="mrd"
    class="h-full flex flex-col bg-white border-l border-gray-200 shadow-lg"
    @keydown.space.prevent="toggleSelection"
    @keydown.up.prevent="$emit('navigate-previous')"
    @keydown.down.prevent="$emit('navigate-next')"
    tabindex="0"
    ref="panelRef"
  >
    <!-- Header -->
    <div class="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
      <div class="flex items-center gap-3">
        <input
          type="checkbox"
          :checked="isSelected"
          @change="toggleSelection"
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
        />
        <!-- Person icon -->
        <svg
          v-if="mrd.reportable_type === 'Person'"
          class="h-5 w-5 text-gray-500"
          stroke="none"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm9 11a1 1 0 0 1-2 0v-2a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v2a1 1 0 0 1-2 0v-2a5 5 0 0 1 5-5h8a5 5 0 0 1 5 5v2z"/>
        </svg>
        <!-- Church icon -->
        <svg
          v-else
          class="h-5 w-5 text-gray-500"
          stroke="none"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.57,14.74a1,1,0,0,0,.69.28,1,1,0,0,0,.72-.31,1,1,0,0,0,0-1.41l-2.3-2.22L18.58,11,13,5.61V4.2h.92a1,1,0,0,0,0-2H13V1.56a1,1,0,0,0-2,0V2.2h-.92a1,1,0,0,0,0,2H11V5.61L3.1,13.3a1,1,0,1,0,1.4,1.44L5.24,14v7.13H4.36a1,1,0,0,0,0,2H19.6a1,1,0,0,0,0-2h-.68v-7ZM7.24,21.15V12.07L12,7.42l4.92,4.76v9Z"/>
        </svg>
        <a
          :href="reportableLink"
          target="_blank"
          class="font-semibold text-gray-900 hover:text-indigo-600"
        >
          {{ mrd.reportable_name }}
        </a>
      </div>
      <button
        @click="$emit('close')"
        class="text-gray-400 hover:text-gray-600 p-1"
      >
        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- Scrollable Content Area -->
    <div class="flex-1 overflow-y-auto">
      <!-- Current MRD Details -->
      <div class="p-4 border-b border-gray-200">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-medium text-gray-500">Current Report</h3>
          <button
            v-if="!isEditing"
            @click="startEditing"
            class="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Edit
          </button>
        </div>
        <div class="text-sm text-gray-600 mb-2">
          {{ formatDate(mrd.report_date) }}
        </div>

        <!-- Read-only mode -->
        <div v-if="!isEditing" class="grid grid-cols-2 gap-3">
          <div class="bg-gray-50 p-3 rounded">
            <div class="text-xs text-gray-500 mb-1">Salvations</div>
            <MrdDeltaIndicator
              :value="mrd.num_salvations || 0"
              :delta="mrd.delta?.num_salvations"
              class="text-lg font-semibold"
            />
          </div>
          <div class="bg-gray-50 p-3 rounded">
            <div class="text-xs text-gray-500 mb-1">Baptisms</div>
            <MrdDeltaIndicator
              :value="mrd.num_baptisms || 0"
              :delta="mrd.delta?.num_baptisms"
              class="text-lg font-semibold"
            />
          </div>
          <div class="bg-gray-50 p-3 rounded">
            <div class="text-xs text-gray-500 mb-1">Widows</div>
            <MrdDeltaIndicator
              :value="mrd.num_widows || 0"
              :delta="mrd.delta?.num_widows"
              class="text-lg font-semibold"
            />
          </div>
          <div class="bg-gray-50 p-3 rounded">
            <div class="text-xs text-gray-500 mb-1">Orphans</div>
            <MrdDeltaIndicator
              :value="mrd.num_orphans || 0"
              :delta="mrd.delta?.num_orphans"
              class="text-lg font-semibold"
            />
          </div>
        </div>

        <!-- Edit mode -->
        <div v-else class="grid grid-cols-2 gap-3">
          <div class="bg-yellow-50 p-3 rounded border border-yellow-200">
            <label class="text-xs text-gray-500 mb-1 block">Salvations</label>
            <input v-model.number="editForm.num_salvations" type="number" min="0"
              class="w-full text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div class="bg-yellow-50 p-3 rounded border border-yellow-200">
            <label class="text-xs text-gray-500 mb-1 block">Baptisms</label>
            <input v-model.number="editForm.num_baptisms" type="number" min="0"
              class="w-full text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div class="bg-yellow-50 p-3 rounded border border-yellow-200">
            <label class="text-xs text-gray-500 mb-1 block">Widows</label>
            <input v-model.number="editForm.num_widows" type="number" min="0"
              class="w-full text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div class="bg-yellow-50 p-3 rounded border border-yellow-200">
            <label class="text-xs text-gray-500 mb-1 block">Orphans</label>
            <input v-model.number="editForm.num_orphans" type="number" min="0"
              class="w-full text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div class="col-span-2 flex gap-2 mt-1">
            <button @click="saveEdit" :disabled="loading || !isDirty"
              class="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50">
              Save
            </button>
            <button @click="cancelEditing"
              class="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
              Cancel
            </button>
          </div>
          <div v-if="editError" class="col-span-2 text-xs text-red-600 mt-1">{{ editError }}</div>
        </div>

        <!-- Metadata -->
        <div class="mt-3 space-y-1 text-xs text-gray-500">
          <div>Submitted by: {{ mrd.submitted_by || 'Unknown' }}</div>
          <div v-if="mrd.validated_at">
            Validated: {{ formatDate(mrd.validated_at) }}
            <span v-if="mrd.validated_by_user_name"> by {{ mrd.validated_by_user_name }}</span>
          </div>
          <div>State: <span :class="mrd.state === 'validated' ? 'text-green-600 font-medium' : 'text-yellow-600 font-medium'">{{ mrd.state }}</span></div>
        </div>
      </div>

      <!-- Previous MRDs History -->
      <div class="p-4">
        <h3 class="text-sm font-medium text-gray-500 mb-3">
          History ({{ previousMrds.length }} previous reports)
        </h3>
        <div v-if="previousMrds.length === 0" class="text-sm text-gray-400 text-center py-4">
          No previous reports found
        </div>
        <div v-else class="space-y-3">
          <div
            v-for="(prevMrd, index) in previousMrds"
            :key="prevMrd.uuid"
            class="bg-gray-50 p-3 rounded border border-gray-100"
          >
            <div class="text-sm text-gray-600 mb-2 font-medium">
              {{ formatDate(prevMrd.report_date) }}
            </div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span class="text-gray-500">Salvations:</span>
                <MrdDeltaIndicator
                  :value="prevMrd.num_salvations || 0"
                  :delta="prevMrd.delta?.num_salvations"
                />
              </div>
              <div>
                <span class="text-gray-500">Baptisms:</span>
                <MrdDeltaIndicator
                  :value="prevMrd.num_baptisms || 0"
                  :delta="prevMrd.delta?.num_baptisms"
                />
              </div>
              <div>
                <span class="text-gray-500">Widows:</span>
                <MrdDeltaIndicator
                  :value="prevMrd.num_widows || 0"
                  :delta="prevMrd.delta?.num_widows"
                />
              </div>
              <div>
                <span class="text-gray-500">Orphans:</span>
                <MrdDeltaIndicator
                  :value="prevMrd.num_orphans || 0"
                  :delta="prevMrd.delta?.num_orphans"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="p-4 border-t border-gray-200 bg-gray-50 space-y-2">
      <!-- Primary actions: Validate + Include -->
      <div class="flex gap-3">
        <button
          @click="$emit('validate', mrd)"
          :disabled="mrd.state === 'validated' || loading"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors inline-flex items-center justify-center"
          :class="mrd.state === 'validated'
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'"
        >
          <svg class="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          Validate (V)
        </button>
        <button
          @click="$emit('include', mrd)"
          :disabled="loading || mrd.included_in_tcr"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 inline-flex items-center justify-center"
          :class="mrd.included_in_tcr
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'"
        >
          <svg v-if="mrd.included_in_tcr" class="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          <svg v-else class="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
          </svg>
          {{ mrd.included_in_tcr ? 'Included' : 'Include (I)' }}
        </button>
      </div>

      <!-- Undo actions: Un-validate + Un-include (shown only when applicable) -->
      <div v-if="mrd.state === 'validated' || mrd.included_in_tcr" class="flex gap-3">
        <button
          v-if="mrd.included_in_tcr"
          @click="confirmUnexclude"
          :disabled="loading"
          class="flex-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50 inline-flex items-center justify-center"
        >
          <svg class="h-3.5 w-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
          Un-include
        </button>
        <button
          v-if="mrd.state === 'validated'"
          @click="confirmUnvalidate"
          :disabled="loading"
          class="flex-1 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 disabled:opacity-50 inline-flex items-center justify-center"
        >
          <svg class="h-3.5 w-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
          Un-validate
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import MrdDeltaIndicator from './MrdDeltaIndicator.vue'

const props = defineProps({
  mrd: {
    type: Object,
    default: null
  },
  previousMrds: {
    type: Array,
    default: () => []
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'close', 'validate', 'include', 'toggle-selection',
  'navigate-previous', 'navigate-next',
  'unvalidate', 'unexclude', 'update-mrd'
])

const panelRef = ref(null)
const isEditing = ref(false)
const editError = ref(null)
const editForm = ref({
  num_salvations: 0,
  num_baptisms: 0,
  num_widows: 0,
  num_orphans: 0
})

// Reset edit mode when MRD changes
watch(() => props.mrd?.uuid, () => {
  isEditing.value = false
  editError.value = null
})

// Save is only enabled while the in-progress edit differs from the persisted
// values. After a successful save, props.mrd updates to the new values and
// editForm stays in sync, so isDirty flips back to false automatically.
const isDirty = computed(() => {
  if (!isEditing.value || !props.mrd) return false
  return (
    editForm.value.num_salvations !== (props.mrd.num_salvations || 0) ||
    editForm.value.num_baptisms !== (props.mrd.num_baptisms || 0) ||
    editForm.value.num_widows !== (props.mrd.num_widows || 0) ||
    editForm.value.num_orphans !== (props.mrd.num_orphans || 0)
  )
})

const reportableLink = computed(() => {
  if (!props.mrd) return '#'
  const type = props.mrd.reportable_type?.toLowerCase()
  const id = props.mrd.reportable_id
  if (type === 'person') {
    return `/people/${id}`
  } else if (type === 'church') {
    return `/churches/${id}`
  }
  return '#'
})

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const toggleSelection = () => {
  emit('toggle-selection', props.mrd?.uuid)
}

const startEditing = () => {
  editForm.value = {
    num_salvations: props.mrd.num_salvations || 0,
    num_baptisms: props.mrd.num_baptisms || 0,
    num_widows: props.mrd.num_widows || 0,
    num_orphans: props.mrd.num_orphans || 0
  }
  editError.value = null
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
  editError.value = null
}

const saveEdit = () => {
  editError.value = null
  emit('update-mrd', { uuid: props.mrd.uuid, ...editForm.value })
}

const confirmUnexclude = () => {
  if (confirm('Remove this MRD from the Training Center Report? The TCR metrics will be recalculated.')) {
    emit('unexclude', props.mrd)
  }
}

const confirmUnvalidate = () => {
  const msg = props.mrd.included_in_tcr
    ? 'Un-validate this MRD? It will also be un-included from its TCR and metrics will be recalculated.'
    : 'Revert this MRD to awaiting validation?'
  if (confirm(msg)) {
    emit('unvalidate', props.mrd)
  }
}

onMounted(() => {
  if (panelRef.value) {
    panelRef.value.focus()
  }
})
</script>
